import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { Button, Grid, Typography } from "@mui/material"
import { faPen } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CreateUpdateProductFormValues, DiscountType, OfferDetailsType } from "../../productsTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { toDinero, toNumberLossy } from "../../../../../currency/currencyLib"
import { useDateTime } from "../../../../../localization/useDateTime"
import { useMoney } from "../../../../../currency/useMoney"
import { OfferDialog } from "./OfferDialog"

const useStyles = makeStyles()((theme) => ({
    title: {
        color: theme.palette.tailwind.slate[600],
    },
    button: {
        textTransform: "none",
        borderRadius: "0.5rem",
        padding: "0.125rem 0.75rem",
        color: "#2196F3",
        borderColor: "#2196F3",
    },
}))

type OfferDetailsProps = Readonly<{
    dialogState: boolean
    setDialogState: React.Dispatch<React.SetStateAction<boolean>>
    setCheckedOffer: React.Dispatch<React.SetStateAction<boolean>>
    onDialogClose?: () => void
    onSaveOffer?: (data: OfferDetailsType) => void
}>

export function OfferDetails({ setCheckedOffer, dialogState, setDialogState, onDialogClose, onSaveOffer }: OfferDetailsProps) {
    const { values, setFieldValue } = useFormikContext<CreateUpdateProductFormValues>()
    const { offerDetails } = values
    const moneyFactory = useMoney({})
    const dateTimeFactory = useDateTime()
    const { classes } = useStyles()

    const isPercentageDiscount = offerDetails?.discount?.type === DiscountType.DISCOUNT_PERCENTAGE

    const toPercentage = (amount: number | undefined, scale: number | undefined) => {
        return (amount || 0) / Math.pow(10, scale || 0)
    }

    const initialValues = (offerDetailsItem?: OfferDetailsType) => {
        if (!offerDetailsItem) {
            return offerDetailsItem
        } else {
            const { discount, availabilityDetails } = offerDetailsItem

            if (!discount) {
                return {
                    ...offerDetailsItem,
                    discount: {
                        type: DiscountType.NONE,
                    },
                    availabilityDetails: {
                        dateTimeInterval: {
                            from: availabilityDetails?.dateTimeInterval?.from
                                ? dateTimeFactory.fromApi(availabilityDetails.dateTimeInterval.from)
                                : undefined,
                            to: availabilityDetails?.dateTimeInterval?.to
                                ? dateTimeFactory.fromApi(availabilityDetails.dateTimeInterval.to)
                                : undefined,
                        },
                    },
                }
            }

            const { percentage, discountedPrice, discountAmount, originalPrice, ...restValues } = discount

            const dateTimeIntervalFrom = availabilityDetails?.dateTimeInterval?.from
                ? dateTimeFactory.fromApi(availabilityDetails.dateTimeInterval.from)
                : undefined
            const dateTimeIntervalTo = availabilityDetails?.dateTimeInterval?.to
                ? dateTimeFactory.fromApi(availabilityDetails.dateTimeInterval.to)
                : undefined

            const availabilityDetailsValue = {
                dateTimeInterval: {
                    from: dateTimeIntervalFrom,
                    to: dateTimeIntervalTo,
                },
            }

            const convertedOriginalPrice = originalPrice ? toNumberLossy(toDinero(originalPrice)) : undefined

            if (discount.type === DiscountType.DISCOUNT_PERCENTAGE) {
                return {
                    ...offerDetailsItem,
                    discount: {
                        ...restValues,
                        originalPrice: convertedOriginalPrice,
                        percentage: {
                            amount: toPercentage(percentage?.amount, percentage?.scale).toString(),
                        },
                    },
                    availabilityDetails: availabilityDetailsValue,
                }
            }
            if (discount.type === DiscountType.DISCOUNT_PRICE) {
                return {
                    ...offerDetailsItem,
                    discount: {
                        ...restValues,
                        originalPrice: convertedOriginalPrice,
                        discountedPrice: discountedPrice ? toNumberLossy(toDinero(discountedPrice)) : undefined,
                    },
                    availabilityDetails: availabilityDetailsValue,
                }
            }

            return {
                ...offerDetailsItem,
                discount: {
                    ...restValues,
                    originalPrice: convertedOriginalPrice,
                },
                availabilityDetails: availabilityDetailsValue,
            }
        }
    }

    return (
        <>
            {offerDetails && (
                <>
                    <Grid container gap={1} flexDirection="column" width="100%" className={classes.title}>
                        <Grid container gap={2} alignItems="center" justifyContent="space-between" flexWrap="nowrap">
                            {isPercentageDiscount ? (
                                <LocalizedStrict id="product-create-offer-discount-percentage">
                                    <Typography variant="subtitle2">Discount percentage:</Typography>
                                </LocalizedStrict>
                            ) : (
                                <LocalizedStrict id="product-create-offer-discount-price">
                                    <Typography variant="subtitle2">New price:</Typography>
                                </LocalizedStrict>
                            )}
                            <Typography variant="subtitle2">
                                {isPercentageDiscount
                                    ? offerDetails.discount?.percentage
                                        ? `${toPercentage(
                                              offerDetails.discount.percentage.amount,
                                              offerDetails.discount.percentage.scale,
                                          )}%`
                                        : "-"
                                    : offerDetails.discount?.discountedPrice
                                      ? moneyFactory.format(toDinero(offerDetails.discount.discountedPrice))
                                      : "-"}
                            </Typography>
                        </Grid>
                        <Grid container gap={2} alignItems="center" justifyContent="space-between" flexWrap="nowrap">
                            <LocalizedStrict id="product-create-offer-available-from">
                                <Typography variant="subtitle2">Available from:</Typography>
                            </LocalizedStrict>
                            <Typography variant="subtitle2">
                                {offerDetails.availabilityDetails?.dateTimeInterval?.from
                                    ? dateTimeFactory.formatDateTime(
                                          dateTimeFactory.fromApi(
                                              offerDetails.availabilityDetails.dateTimeInterval.from,
                                          ),
                                      )
                                    : "-"}
                            </Typography>
                        </Grid>
                        <Grid container gap={2} alignItems="center" justifyContent="space-between" flexWrap="nowrap">
                            <LocalizedStrict id="product-create-offer-available-to">
                                <Typography variant="subtitle2">Available to:</Typography>
                            </LocalizedStrict>
                            <Typography variant="subtitle2">
                                {offerDetails.availabilityDetails?.dateTimeInterval?.to
                                    ? dateTimeFactory.formatDateTime(
                                          dateTimeFactory.fromApi(offerDetails.availabilityDetails.dateTimeInterval.to),
                                      )
                                    : "-"}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container justifyContent="flex-end">
                        <LocalizedStrict id="product-create-offer-edit-button">
                            <Button
                                onClick={() => setDialogState(!dialogState)}
                                color="primary"
                                variant="outlined"
                                startIcon={<FontAwesomeIcon icon={faPen} />}
                                size="small"
                                className={classes.button}
                            >
                                Edit
                            </Button>
                        </LocalizedStrict>
                    </Grid>
                </>
            )}
            {dialogState && (
                <OfferDialog
                    open={dialogState}
                    onClose={onDialogClose ?? (() => setDialogState(false))}
                    initialValues={initialValues(offerDetails)}
                    handleSaveOffer={onSaveOffer ?? ((data) => {
                        setFieldValue("offerDetails", data)
                        setCheckedOffer(true)
                    })}
                />
            )}
        </>
    )
}
